// Presentation for an interview-invite RSVP.
//
// Written by the backend's services/rsvpSync.js from the iCalendar reply the
// candidate's mail client sends when they click Yes / No / Maybe.
//
// IMPORTANT — "no response" is not a negative signal. Gmail's web UI offers
// RSVP buttons on an .ics invite, but the Gmail mobile app frequently does not,
// and plenty of people simply never click. A silent candidate is the NORMAL
// case, not an unresponsive one, so the absent state reads "Awaiting reply"
// rather than "No response" / "Not attending". Recruiters act on these labels.

const META = {
  accepted:  { icon: '✓', label: 'Accepted', color: '#10b981' },
  declined:  { icon: '✕', label: 'Declined', color: '#ef4444' },
  tentative: { icon: '?', label: 'Maybe',    color: '#f59e0b' },
};

/** Style + label for a stored response, or null when it isn't one we know. */
export const rsvpMeta = (response) => META[response] || null;

/** "3 Aug, 1:05 pm" — when they answered. Null for missing/malformed. */
export const formatRespondedAt = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true,
  });
};

// "shivam.raghuwanshi" → "Shivam Raghuwanshi". Only a fallback: the real CN
// from the reply is preferred when we have it.
const nameFromEmail = (email) =>
  String(email || '').split('@')[0]
    .split(/[._+-]+/).filter(Boolean)
    .map(p => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ') || email;

/**
 * One row per interviewer on a round: who was invited, and what they said.
 *
 * Built from the INVITED list (round.interviewerEmails) rather than from the
 * replies, so an interviewer who has not answered still appears — otherwise a
 * round with two interviewers and one reply would look like it only ever had
 * one. Anyone who replied but is no longer on the invite list (removed after
 * answering) is appended so their answer is not silently dropped.
 *
 * Returns [] when nobody was invited, so callers can render nothing.
 */
export const interviewerRoster = (round) => {
  const responses = round?.attendeeResponses || {};
  const invited = (Array.isArray(round?.interviewerEmails) ? round.interviewerEmails : [])
    .map(e => String(e || '').trim().toLowerCase())
    .filter(Boolean);

  const seen = new Set();
  const rows = [];
  for (const email of [...invited, ...Object.keys(responses)]) {
    if (seen.has(email)) continue;
    seen.add(email);
    const r = responses[email];
    rows.push({
      email,
      name: r?.name || nameFromEmail(email),
      response: r?.response || null,
      at: r?.at || null,
    });
  }
  return rows;
};
